import { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { toPng, toBlob } from 'html-to-image';
import QRCode from 'qrcode';
import {
  ImageIcon,
  AlignLeft,
  Instagram,
  Facebook,
  Save,
  Link2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import StoryCard, { STORY_CARD_THEMES, STORY_CARD_ASPECTS } from './StoryCard';
import {
  buildShareText,
  canShareFiles,
  canUseNativeShare,
  facebookShareUrl,
  formatFromTo,
  getDefaultPullQuote,
  HIGHLIGHT_MAX,
} from '@/lib/storyShare';
import { useTagOptions } from '@/hooks/useTagOptions';

const ASPECT_OPTIONS = [
  STORY_CARD_ASPECTS.square,
  STORY_CARD_ASPECTS.story,
];

const slugify = (s) =>
  (s || 'story')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'story';

const WhatsAppGlyph = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.73 1.21h.01c5.46 0 9.9-4.45 9.91-9.91a9.86 9.86 0 0 0-2.9-7.01zm-7 15.24h-.01a8.23 8.23 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.21 8.21 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.7-.8-.23-.08-.39-.12-.56.13-.17.25-.64.8-.79.97-.15.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.38-1.72-.15-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.49-.41-.42-.56-.43l-.48-.01c-.16 0-.43.06-.66.31-.23.25-.86.85-.86 2.07s.88 2.4.99 2.57c.12.16 1.74 2.66 4.21 3.73.59.25 1.05.4 1.41.51.59.19 1.13.16 1.55.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.11-.22-.17-.47-.29z" />
  </svg>
);

const XGlyph = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2H21.5l-7.5 8.572L23 22h-6.781l-5.31-6.94L4.8 22H1.54l8.04-9.19L1 2h6.91l4.79 6.34L18.244 2zm-2.378 18h1.853L7.27 4H5.31l10.556 16z" />
  </svg>
);

const StoryCardDialog = ({ open, onOpenChange, story, shareUrl }) => {
  const { tagOptions } = useTagOptions();
  const [theme, setTheme] = useState('gradient');
  const [aspect, setAspect] = useState('square');
  const aspectDims = STORY_CARD_ASPECTS[aspect] || STORY_CARD_ASPECTS.square;
  const [showAuthor, setShowAuthor] = useState(true);
  const [showPullQuote, setShowPullQuote] = useState(true);
  const [showQr, setShowQr] = useState(true);
  const [pullQuote, setPullQuote] = useState('');
  const [caption, setCaption] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  // Brand logo is inlined as a data URL so html-to-image rasterizes it
  // deterministically — same trick as the QR. A raw <img src="/logo.png">
  // can race the toPng() call on first open if the network is slow.
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [busy, setBusy] = useState('');
  const cardRef = useRef(null);
  const previewWrapRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(360);

  // Resolve obstacle + inner_quality tag IDs to display names. We pass
  // arrays (not just the first) so the from→to line honors stories
  // where two obstacles/qualities genuinely co-emerged — formatFromTo
  // caps the visible count at 2 per side so the line stays catchy.
  const fromToLabels = useMemo(() => {
    const resolveAll = (ids, opts) => {
      if (!Array.isArray(ids)) return [];
      const lookup = opts || [];
      return ids
        .map((id) => lookup.find((t) => t.id === id)?.display_name || id)
        .filter(Boolean);
    };
    return {
      obstacles: resolveAll(story?.obstacles, tagOptions?.obstacles),
      qualities: resolveAll(story?.inner_qualities, tagOptions?.inner_qualities),
    };
  }, [story, tagOptions]);

  // First profession (if any), resolved to its display label. Emoji is
  // intentionally omitted on the share-card export path — emojis render
  // inconsistently through html-to-image / canvas serialization, so
  // sticking to plain text keeps exports reliable across platforms.
  const professionLabel = useMemo(() => {
    const id = story?.professions?.[0];
    if (!id) return '';
    const opts = tagOptions?.professions || [];
    return opts.find((t) => t.id === id)?.display_name || '';
  }, [story, tagOptions]);

  const fromToDisplay = useMemo(() => formatFromTo(fromToLabels), [fromToLabels]);

  // Seed pull quote + caption on open. Re-seed if tag display names resolve
  // asynchronously — but never overwrite a caption the user has edited.
  const lastSeededCaptionRef = useRef('');
  const seededOnceRef = useRef(false);
  useEffect(() => {
    if (!open) {
      lastSeededCaptionRef.current = '';
      seededOnceRef.current = false;
      return;
    }
    const fresh = buildShareText(story, 'whatsapp', shareUrl, fromToLabels);
    setCaption((prev) => {
      if (!seededOnceRef.current) {
        seededOnceRef.current = true;
        lastSeededCaptionRef.current = fresh;
        return fresh;
      }
      if (prev === lastSeededCaptionRef.current && prev !== fresh) {
        lastSeededCaptionRef.current = fresh;
        return fresh;
      }
      return prev;
    });
    setPullQuote((prev) => prev || getDefaultPullQuote(story));
  }, [open, story, shareUrl, fromToLabels]);

  // Load brand emblem once per dialog open. Same-origin fetch → FileReader
  // gives us a data URL we can embed straight into the card, sidestepping
  // image-decode timing issues during export.
  useEffect(() => {
    if (!open || logoDataUrl) return undefined;
    let cancelled = false;
    fetch('/logo.png')
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(`logo ${res.status}`))))
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          })
      )
      .then((dataUrl) => {
        if (!cancelled && typeof dataUrl === 'string') setLogoDataUrl(dataUrl);
      })
      .catch((err) => {
        console.error('Brand logo load failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, logoDataUrl]);

  // Generate QR for current URL.
  useEffect(() => {
    if (!open || !shareUrl) return;
    let cancelled = false;
    QRCode.toDataURL(shareUrl, {
      margin: 0,
      width: 336,
      color: { dark: '#2D2A26', light: '#FFFFFF' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch((err) => {
        console.error('QR generation failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, shareUrl]);

  // Responsive preview: ResizeObserver tracks the wrapper's actual rendered
  // width, so the scaled card fills it exactly. Aspect changes the wrapper's
  // maxWidth + aspect-ratio synchronously; re-running this effect on aspect
  // forces a layout flush via clientWidth read, so previewScale is correct
  // on the very next render — no stale-scale flash.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const node = previewWrapRef.current;
    if (!node) return undefined;
    const sync = () => {
      const w = node.clientWidth;
      if (w) setPreviewWidth(w);
    };
    sync();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', sync);
      return () => window.removeEventListener('resize', sync);
    }
    const ro = new ResizeObserver(sync);
    ro.observe(node);
    return () => ro.disconnect();
  }, [open, aspect]);

  const previewScale = previewWidth / aspectDims.width;

  const renderPng = async () => {
    if (!cardRef.current) throw new Error('Card not mounted');
    return toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 1,
      width: aspectDims.width,
      height: aspectDims.height,
    });
  };

  const renderBlob = async () => {
    if (!cardRef.current) throw new Error('Card not mounted');
    return toBlob(cardRef.current, {
      cacheBust: true,
      pixelRatio: 1,
      width: aspectDims.width,
      height: aspectDims.height,
    });
  };

  const downloadDataUrl = (dataUrl) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `katha-mithra-${slugify(story?.title)}-${aspect}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // When using navigator.share with a `url` field, most receivers (WhatsApp,
  // Messages, Mail) append the url separately — so leaving it inside the
  // caption makes it appear twice. Strip it here for those callsites.
  const captionWithoutUrl = () => {
    if (!shareUrl) return caption;
    return caption
      .split(shareUrl).join('')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // True when we should route Quick-Share tiles through the OS share sheet
  // with the image attached (mobile-class browsers that accept files via
  // Web Share). Desktop browsers fall through to platform-specific deep links.
  const canShareImageNatively = () =>
    canUseNativeShare() &&
    typeof navigator.canShare === 'function' &&
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(pointer: coarse)')?.matches;

  const renderFile = async () => {
    const blob = await renderBlob();
    if (!blob) return null;
    return new File([blob], `katha-mithra-${slugify(story?.title)}.png`, {
      type: 'image/png',
    });
  };

  // Attempt image + caption via the OS share sheet. Returns true on success
  // (or user-cancelled abort) so the caller knows not to run a fallback.
  const tryNativeImageShare = async () => {
    if (!canShareImageNatively()) return false;
    try {
      const file = await renderFile();
      if (!file || !canShareFiles([file])) return false;
      await navigator.share({
        files: [file],
        text: captionWithoutUrl(),
        url: shareUrl,
      });
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return true;
      console.error('Native image share failed:', err);
      return false;
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      console.error('Clipboard write failed:', err);
    }
    return false;
  };

  const handleSave = async () => {
    setBusy('save');
    try {
      const dataUrl = await renderPng();
      downloadDataUrl(dataUrl);
      toast.success('Story card saved');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Could not generate the card.');
    } finally {
      setBusy('');
    }
  };

  const handleShareAsImage = async () => {
    setBusy('image');
    try {
      const blob = await renderBlob();
      if (!blob) throw new Error('No blob');
      const file = new File([blob], `katha-mithra-${slugify(story?.title)}.png`, {
        type: 'image/png',
      });
      if (canUseNativeShare() && canShareFiles([file])) {
        await navigator.share({
          files: [file],
          title: story?.title || 'Katha Mithra',
          text: captionWithoutUrl(),
          url: shareUrl,
        });
      } else {
        // Desktop / unsupported: download instead.
        const dataUrl = URL.createObjectURL(blob);
        downloadDataUrl(dataUrl);
        URL.revokeObjectURL(dataUrl);
        toast.success('Image downloaded — your browser does not support direct image sharing.');
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Share as image failed:', err);
      toast.error('Could not share the image.');
    } finally {
      setBusy('');
    }
  };

  const handleShareAsText = async () => {
    setBusy('text');
    try {
      if (canUseNativeShare()) {
        await navigator.share({
          title: story?.title || 'Katha Mithra',
          text: captionWithoutUrl(),
          url: shareUrl,
        });
      } else {
        const ok = await copyToClipboard(`${caption}`);
        toast[ok ? 'success' : 'error'](
          ok ? 'Caption copied to clipboard' : 'Could not copy caption'
        );
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Share as text failed:', err);
      toast.error('Could not share text.');
    } finally {
      setBusy('');
    }
  };

  // Direct WhatsApp deep link — one tap, opens WhatsApp with caption + URL
  // prefilled. WhatsApp's URL scheme doesn't accept image attachments from a
  // web page; users who want the actual PNG attached use "Share as Image"
  // above, which routes through the OS share sheet. Once the story page has
  // proper og:image meta, WhatsApp will render the card inline as a link
  // preview from the URL.
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(caption)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // Instagram has no web upload endpoint. On mobile with native file-share,
  // open the OS share sheet with the image attached — the user picks
  // Instagram from there and IG opens with the image preloaded. On desktop or
  // unsupported mobile browsers, fall back to downloading the image so the
  // user can post manually.
  const handleInstagram = async () => {
    setBusy('instagram');
    try {
      await copyToClipboard(caption);
      if (canShareImageNatively()) {
        const shared = await tryNativeImageShare();
        if (shared) {
          toast.success('Caption copied — paste it after picking Instagram.');
          return;
        }
      }
      // Desktop or no file share: IG has no web upload endpoint, so just
      // save the image + copy caption and let the user post manually.
      const blob = await renderBlob();
      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        downloadDataUrl(objectUrl);
        URL.revokeObjectURL(objectUrl);
      }
      toast.success('Image saved + caption copied. Open Instagram and post.');
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Instagram prep failed:', err);
      toast.error('Could not prepare Instagram share.');
    } finally {
      setBusy('');
    }
  };

  const handleFacebook = () => {
    // sharer.php takes only the URL — Facebook scrapes og:image from the page
    // to render the preview card. Copy caption for the user to paste.
    window.open(
      facebookShareUrl(shareUrl),
      '_blank',
      'noopener,noreferrer,width=600,height=600'
    );
    copyToClipboard(caption).then((ok) => {
      if (ok) toast.success('Caption copied — paste it in Facebook.');
    });
  };

  const handleTwitter = () => {
    // X / Twitter web intent — strip URL from text since the intent appends
    // it via the `url` param to avoid duplication.
    const text = caption.replace(shareUrl, '').trim();
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer,width=600,height=600'
    );
  };

  const handleCopyLink = async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) toast.success('Link copied to clipboard.');
    else toast.error('Could not copy the link.');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="story-card-dialog"
      >
        <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-[#E6E2D6]">
          <DialogTitle className="text-xl sm:text-2xl font-serif text-[#2D2A26]">
            Share this story
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-6">
          {/* Preview */}
          <div>
            <div className="text-xs font-medium tracking-wide uppercase text-[#6B665E] mb-2">
              Preview
            </div>
            <div className="flex justify-center">
              <div
                ref={previewWrapRef}
                className="relative w-full rounded-2xl overflow-hidden shadow-md border border-[#E6E2D6]"
                style={{
                  maxWidth: aspect === 'story' ? 300 : 480,
                  aspectRatio: aspectDims.ratio,
                }}
              >
                <StoryCard
                  ref={cardRef}
                  story={story}
                  theme={theme}
                  aspect={aspect}
                  pullQuote={pullQuote}
                  showAuthor={showAuthor}
                  showPullQuote={showPullQuote}
                  showQr={showQr}
                  qrDataUrl={qrDataUrl}
                  brandLogoUrl={logoDataUrl}
                  fromTo={fromToDisplay}
                  professionLabel={professionLabel}
                  previewScale={previewScale}
                />
              </div>
            </div>
            <p className="text-xs text-[#6B665E] text-center mt-2">
              {aspectDims.width} × {aspectDims.height} PNG
            </p>
          </div>

          {/* Customize */}
          <section className="space-y-4">
            <div className="text-xs font-medium tracking-wide uppercase text-[#6B665E]">
              Customize
            </div>
            <div>
              <div className="text-sm font-medium text-[#2D2A26] mb-2">Format</div>
              <div className="grid grid-cols-2 gap-2">
                {ASPECT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setAspect(opt.id)}
                    data-testid={`card-aspect-${opt.id}`}
                    className={`flex items-center gap-3 rounded-lg border p-2.5 text-left transition ${
                      aspect === opt.id
                        ? 'border-[#C05621] bg-[#FBF4EB]'
                        : 'border-[#E6E2D6] hover:bg-[#FBF4EB]/60'
                    }`}
                  >
                    <AspectSwatch id={opt.id} />
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-[#2D2A26]">{opt.label}</span>
                      <span className="text-[10px] text-[#6B665E]">
                        {opt.id === 'square' ? 'Feed · Messages' : 'WA Status · IG Story'}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-[#2D2A26] mb-2">Theme</div>
              <div className="grid grid-cols-4 gap-2">
                {STORY_CARD_THEMES.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTheme(opt.id)}
                    data-testid={`card-theme-${opt.id}`}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border p-2 text-xs transition ${
                      theme === opt.id
                        ? 'border-[#C05621] bg-[#FBF4EB]'
                        : 'border-[#E6E2D6] hover:bg-[#FBF4EB]/60'
                    }`}
                  >
                    <ThemeSwatch id={opt.id} />
                    <span className="text-[#2D2A26]">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <ToggleRow id="card-show-author" label="Author" checked={showAuthor} onCheckedChange={setShowAuthor} />
              <ToggleRow id="card-show-quote" label="Pull quote" checked={showPullQuote} onCheckedChange={setShowPullQuote} />
              <ToggleRow id="card-show-qr" label="QR code" checked={showQr} onCheckedChange={setShowQr} />
            </div>

            {showPullQuote && (
              <div className="space-y-1.5">
                <Label htmlFor="card-pull-quote" className="text-sm">
                  Pull quote
                </Label>
                <Textarea
                  id="card-pull-quote"
                  value={pullQuote}
                  onChange={(e) => setPullQuote(e.target.value.slice(0, HIGHLIGHT_MAX))}
                  rows={4}
                  className="resize-none"
                  data-testid="card-pull-quote-input"
                />
                <p className="text-xs text-[#6B665E]">{pullQuote.length}/{HIGHLIGHT_MAX}</p>
              </div>
            )}
          </section>

          {/* Caption */}
          <section className="space-y-1.5">
            <Label htmlFor="share-caption" className="text-xs font-medium tracking-wide uppercase text-[#6B665E]">
              Caption
            </Label>
            <Textarea
              id="share-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={5}
              className="resize-none text-sm"
              data-testid="share-caption-input"
            />
            <p className="text-xs text-[#6B665E]">
              Edit once — this caption is used by every share option below.
            </p>
          </section>

          {/* Share As */}
          <section className="space-y-2">
            <div className="text-xs font-medium tracking-wide uppercase text-[#6B665E]">
              Share as
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ShareAsButton
                onClick={handleShareAsImage}
                disabled={!!busy}
                busy={busy === 'image'}
                icon={<ImageIcon className="h-5 w-5 text-[#C05621]" />}
                label="Share as Image"
                testid="share-as-image-btn"
              />
              <ShareAsButton
                onClick={handleShareAsText}
                disabled={!!busy}
                busy={busy === 'text'}
                icon={<AlignLeft className="h-5 w-5 text-[#4A5D4F]" />}
                label="Share as Text"
                testid="share-as-text-btn"
              />
            </div>
          </section>

          {/* Quick Share */}
          <section className="space-y-2 pb-2">
            <div className="text-xs font-medium tracking-wide uppercase text-[#6B665E]">
              Quick share
            </div>
            <div className="grid grid-cols-3 gap-2">
              <QuickTile
                onClick={handleWhatsApp}
                disabled={!!busy}
                busy={busy === 'whatsapp'}
                icon={<WhatsAppGlyph className="h-6 w-6 text-[#25D366]" />}
                label="WhatsApp"
                testid="quick-whatsapp"
              />
              <QuickTile
                onClick={handleSave}
                disabled={!!busy}
                busy={busy === 'save'}
                icon={<Save className="h-6 w-6 text-[#C05621]" />}
                label="Save"
                testid="quick-save"
              />
              <QuickTile
                onClick={handleInstagram}
                disabled={!!busy}
                busy={busy === 'instagram'}
                icon={<Instagram className="h-6 w-6 text-[#E4405F]" />}
                label="Instagram"
                testid="quick-instagram"
              />
              <QuickTile
                onClick={handleFacebook}
                disabled={!!busy}
                busy={busy === 'facebook'}
                icon={<Facebook className="h-6 w-6 text-[#1877F2]" />}
                label="Facebook"
                testid="quick-facebook"
              />
              <QuickTile
                onClick={handleTwitter}
                disabled={!!busy}
                busy={false}
                icon={<XGlyph className="h-5 w-5 text-[#2D2A26]" />}
                label="X"
                testid="quick-x"
              />
              <QuickTile
                onClick={handleCopyLink}
                disabled={!!busy}
                busy={false}
                icon={<Link2 className="h-6 w-6 text-[#6B665E]" />}
                label="Copy link"
                testid="quick-copy-link"
              />
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ToggleRow = ({ id, label, checked, onCheckedChange }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-[#E6E2D6] px-3 py-2">
    <Label htmlFor={id} className="text-sm text-[#2D2A26] cursor-pointer">
      {label}
    </Label>
    <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

const ShareAsButton = ({ onClick, disabled, busy, icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="flex items-center gap-3 rounded-lg border border-[#E6E2D6] bg-white px-4 py-3 text-left transition hover:bg-[#FBF4EB] focus:outline-none focus:ring-2 focus:ring-[#C05621] disabled:opacity-60"
  >
    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#FBF4EB]">
      {icon}
    </span>
    <span className="text-sm font-medium text-[#2D2A26]">
      {busy ? 'Working…' : label}
    </span>
  </button>
);

const QuickTile = ({ onClick, disabled, busy, icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="flex flex-col items-center gap-1.5 rounded-lg border border-[#E6E2D6] bg-white px-2 py-3 transition hover:bg-[#FBF4EB] focus:outline-none focus:ring-2 focus:ring-[#C05621] disabled:opacity-60"
  >
    <span className="flex h-9 w-9 items-center justify-center">{icon}</span>
    <span className="text-xs text-[#2D2A26]">{busy ? '…' : label}</span>
  </button>
);

const AspectSwatch = ({ id }) => {
  const isStory = id === 'story';
  return (
    <span
      className="block rounded border border-[#E6E2D6] bg-gradient-to-br from-[#C05621] to-[#E8915B]"
      style={{
        width: isStory ? 16 : 28,
        height: 28,
        flexShrink: 0,
      }}
    />
  );
};

const ThemeSwatch = ({ id }) => {
  const style = {
    gradient: { background: 'linear-gradient(135deg, #C05621 0%, #E8915B 55%, #FDFBF7 100%)' },
    minimal: { background: '#FDFBF7', border: '1px solid #E6E2D6' },
    vibrant: { background: '#4A5D4F' },
    dark: { background: '#2D2A26' },
  }[id];
  return <span className="block h-8 w-8 rounded-md" style={style} />;
};

export default StoryCardDialog;
